import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createSolarAgreementFromQuotation,
  deleteSolarAgreement,
  deleteSolarCommissioning,
  deleteSolarOcularVisit,
  fetchEmployeeFormQuotations,
  fetchSolarAgreementById,
  fetchSolarAgreements,
  fetchSolarCommissioningForms,
  fetchSolarOcularVisits,
  getEmployeeFormsCompany,
  previewSolarAgreementFromQuotation,
  updateSolarAgreement
} from '../../api/forms';
import { fetchDocuments } from '../../api/documents';
import { getCompany } from '../../api/company';
import SolarCommissioningManager from './SolarCommissioningManager';
import SolarOcularVisitManager from './SolarOcularVisitManager';

const TERM_FIELDS = [
  ['purpose', 'Purpose of this agreement'],
  ['projectInvestment', 'Project investment'],
  ['energyEstimate', 'Estimated production and savings'],
  ['paymentConditions', 'Payment conditions'],
  ['creditCardPayment', 'Credit card payment'],
  ['scopeOfWork', 'Scope of work'],
  ['clientResponsibilities', 'Client responsibilities'],
  ['structuralElectrical', 'Structural and electrical conditions'],
  ['changeOrders', 'Change orders'],
  ['testingTurnover', 'Testing, commissioning and turnover'],
  ['loadManagement', 'Load management'],
  ['systemUseMaintenance', 'System use and maintenance'],
  ['ownershipRisk', 'Ownership and risk'],
  ['cancellationSuspension', 'Cancellation and suspension'],
  ['performanceDisclaimer', 'Performance and savings disclaimer'],
  ['liability', 'Liability'],
  ['disputeResolution', 'Dispute resolution and governing law'],
  ['entireAgreement', 'Entire agreement'],
  ['importantClientNote', 'Important client note']
];

const EMPTY_AGREEMENT = {
  status: 'draft',
  quotationReference: '',
  agreementDate: new Date().toISOString().slice(0, 10),
  client: { name: '', address: '', phone: '', email: '' },
  project: {
    systemType: '',
    pvArray: '',
    batteryStorage: '',
    inverter: '',
    contractPrice: 0,
    invoiceStatus: '',
    timeline: ''
  },
  components: [],
  capacity: [],
  paymentSchedule: [],
  creditCardFeeRate: 5,
  creditCardFee: 0,
  creditCardTotal: 0,
  warranty: [],
  terms: {},
  acceptance: {
    clientName: '',
    contractorRepresentative: 'AUTHORIZED REPRESENTATIVE',
    clientDate: '',
    contractorDate: '',
    witnessOne: '',
    witnessTwo: ''
  },
  turnover: {},
  notary: {}
};

function formatMoney(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString('en-PH', {
      timeZone: 'Asia/Manila',
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  } catch {
    return '-';
  }
}

function dateInputValue(value) {
  if (!value) return '';
  try {
    return new Date(value).toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

function recordIncludes(record, term) {
  if (!term) return true;
  return [
    record.number,
    record.reference,
    record.client,
    record.project,
    record.type,
    record.status
  ].some((value) => String(value || '').toLowerCase().includes(term));
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function companyLogoSrc(company = {}) {
  return company.logo || `${window.location.origin}/logo.png`;
}

function cleanFilePart(value, fallback) {
  return String(value || fallback)
    .trim()
    .replace(/[^a-z0-9_-]+/gi, '-')
    .replace(/^-+|-+$/g, '');
}

function agreementPdfFileName(agreement) {
  return [
    cleanFilePart(agreement?.agreementNumber, 'solar-agreement'),
    cleanFilePart(agreement?.project?.projectName || agreement?.project?.systemType || agreement?.quotationReference, 'project'),
    cleanFilePart(agreement?.client?.name, 'client')
  ].filter(Boolean).join('-') + '.pdf';
}

function agreementPrintTitle(agreement) {
  return agreementPdfFileName(agreement).replace(/\.pdf$/i, '');
}

function extractCapacityText(value) {
  const match = String(value || '').match(/(\d+(?:\.\d+)?)\s*kW/i);
  return match ? `${match[1]}kW` : '';
}

function normalizeCapacityRows(capacity = [], project = {}) {
  const rows = Array.isArray(capacity) ? capacity : [];
  const hasInverterCapacity = rows.some((row) => String(row?.label || '').trim().toLowerCase() === 'inverter capacity');
  if (hasInverterCapacity) return rows;

  const inverterCapacityRow = {
    label: 'Inverter Capacity',
    value: extractCapacityText(project.inverter)
  };
  const totalPvIndex = rows.findIndex((row) => String(row?.label || '').trim().toLowerCase() === 'total pv capacity');
  if (totalPvIndex >= 0) {
    return [
      ...rows.slice(0, totalPvIndex + 1),
      inverterCapacityRow,
      ...rows.slice(totalPvIndex + 1)
    ];
  }
  return [...rows, inverterCapacityRow];
}

function agreementClauseHtml(title, value) {
  return `
    <div class="clause">
      <h2>${escapeHtml(title)}</h2>
      <p>${escapeHtml(value || '')}</p>
    </div>
  `;
}

function agreementTableSectionHtml(title, tableHtml) {
  return `
    <div class="print-block">
      <h2>${escapeHtml(title)}</h2>
      ${tableHtml}
    </div>
  `;
}

const AGREEMENT_PRINT_CSS = `
  @page { size: A4 portrait; margin: 12mm 14mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { background: #f3f4f6; color: #111827; font-family: Arial, sans-serif; font-size: 9.5pt; line-height: 1.34; }
  .sheet {
    width: 210mm;
    min-height: 297mm;
    margin: 0 auto;
    padding: 12mm 14mm;
    background: #fff;
  }
  .header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 47mm;
    align-items: start;
    gap: 10mm;
    border-bottom: 1px solid #9ca3af;
    padding-bottom: 7px;
    margin-bottom: 10px;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .agreement-logo {
    width: 47mm;
    height: 34mm;
    overflow: hidden;
    justify-self: end;
    background: #fff;
  }
  .agreement-logo img {
    width: 50mm;
    height: 34mm;
    object-fit: fill;
    transform: translateX(-3mm);
    display: block;
  }
  .agreement-meta {
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr);
    gap: 2px 8px;
    margin-top: 4px;
    font-size: 8.4pt;
    line-height: 1.25;
  }
  .agreement-meta strong {
    white-space: nowrap;
  }
  .agreement-meta span {
    overflow-wrap: anywhere;
  }
  h1 { font-size: 14pt; margin: 0 0 4px; text-transform: uppercase; line-height: 1.18; }
  h2 { font-size: 10pt; margin: 10px 0 4px; text-transform: uppercase; line-height: 1.2; }
  p { margin: 0 0 5px; text-align: justify; orphans: 3; widows: 3; }
  .muted { color: #4b5563; text-align: left; }
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 4px 0 8px;
    break-inside: auto;
    page-break-inside: auto;
  }
  thead { display: table-header-group; }
  tfoot { display: table-footer-group; }
  tr {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  th, td {
    border: 1px solid #d1d5db;
    padding: 3.5px 5px;
    vertical-align: top;
    text-align: left;
  }
  th { background: #f3f4f6; font-weight: 700; }
  .summary th { width: 34%; }
  .print-block,
  .clause,
  .signatures,
  footer {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .signatures {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin: 18px 0 10px;
  }
  .signatures span {
    display: block;
    border-top: 1px solid #111827;
    height: 1px;
    margin: 30px 0 6px;
  }
  .signatures small { display: block; color: #374151; }
  footer {
    margin-top: 14px;
    padding-top: 6px;
    border-top: 1px solid #d1d5db;
    font-size: 8pt;
    color: #4b5563;
  }
  @media screen {
    .sheet { box-shadow: 0 12px 30px rgba(15, 23, 42, 0.14); }
  }
  @media print {
    body { background: #fff; }
    .sheet {
      width: auto;
      min-height: auto;
      margin: 0;
      padding: 0;
      box-shadow: none;
    }
  }
`;

function normalizeAgreement(agreement) {
  const normalized = {
    ...EMPTY_AGREEMENT,
    ...agreement,
    agreementDate: agreement?.agreementDate
      ? new Date(agreement.agreementDate).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10),
    client: { ...EMPTY_AGREEMENT.client, ...(agreement?.client || {}) },
    project: { ...EMPTY_AGREEMENT.project, ...(agreement?.project || {}) },
    components: Array.isArray(agreement?.components) ? agreement.components : [],
    paymentSchedule: Array.isArray(agreement?.paymentSchedule) ? agreement.paymentSchedule : [],
    warranty: Array.isArray(agreement?.warranty) ? agreement.warranty : [],
    terms: { ...EMPTY_AGREEMENT.terms, ...(agreement?.terms || {}) },
    acceptance: { ...EMPTY_AGREEMENT.acceptance, ...(agreement?.acceptance || {}) },
    turnover: { ...EMPTY_AGREEMENT.turnover, ...(agreement?.turnover || {}) },
    notary: { ...EMPTY_AGREEMENT.notary, ...(agreement?.notary || {}) }
  };
  normalized.capacity = normalizeCapacityRows(agreement?.capacity, normalized.project);
  return normalized;
}

function agreementPrintHtml(agreement, company = {}) {
  const client = agreement.client || {};
  const project = agreement.project || {};
  const terms = agreement.terms || {};
  const companyName = company.name || 'iConnect Home Wireless Network & Data Solution / iCONNECT SOLAR';
  const companyAddress = company.address || 'Agunod Building, Lapu-Lapu Street, Magugpo Poblacion, Tagum City, Davao del Norte';
  const companyPhone = company.phone || '09562755878';
  const companyEmail = company.email || 'iconnect0718@gmail.com';
  const logo = companyLogoSrc(company);

  return `
    <section class="sheet">
      <header class="header">
        <div>
          <h1>SOLAR PV SYSTEM SUPPLY, INSTALLATION AND PAYMENT AGREEMENT</h1>
          <p class="muted">Complete project scope, payment schedule, warranty and client acceptance</p>
          <div class="agreement-meta">
            <strong>Agreement No.</strong><span>${escapeHtml(agreement.agreementNumber || '-')}</span>
            <strong>Quotation Reference</strong><span>${escapeHtml(agreement.quotationReference || '-')}</span>
            <strong>Agreement Date</strong><span>${escapeHtml(formatDate(agreement.agreementDate))}</span>
          </div>
        </div>
        <div class="agreement-logo"><img src="${escapeHtml(logo)}" alt="iConnect" /></div>
      </header>

      <p>This Agreement is entered into by and between <strong>${escapeHtml(companyName)}</strong>, with business address at ${escapeHtml(companyAddress)} (the "Contractor"), and <strong>${escapeHtml(client.name || '-')}</strong>, with project address at ${escapeHtml(client.address || '-')} (the "Client").</p>

      ${agreementTableSectionHtml('Project Summary', `
      <table class="summary">
        <tbody>
          ${[
            ['Agreement No.', agreement.agreementNumber],
            ['Client', client.name],
            ['Project Site', client.address],
            ['Contact', [client.phone, client.email].filter(Boolean).join(' / ')],
            ['System Type', project.systemType],
            ['PV Array', project.pvArray],
            ['Battery Storage', project.batteryStorage],
            ['Inverter', project.inverter],
            ['Contract Price', `PHP ${formatMoney(project.contractPrice)}`],
            ['Invoice Status', project.invoiceStatus]
          ].map(([label, value]) => `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value || '-')}</td></tr>`).join('')}
        </tbody>
      </table>
      `)}

      ${agreementClauseHtml('Purpose of this Agreement', terms.purpose)}
      ${agreementTableSectionHtml('System Components and Scope', `
      <table>
        <thead><tr><th>#</th><th>Item / Scope</th><th>Quantity</th></tr></thead>
        <tbody>${(agreement.components || []).map((row, index) => `<tr><td>${index + 1}</td><td>${escapeHtml(row.item)}</td><td>${escapeHtml(row.quantity)}</td></tr>`).join('')}</tbody>
      </table>
      `)}

      ${agreementTableSectionHtml('System Capacity', `
      <table>
        <tbody>${(agreement.capacity || []).map((row) => `<tr><th>${escapeHtml(row.label)}</th><td>${escapeHtml(row.value)}</td></tr>`).join('')}</tbody>
      </table>
      `)}

      ${agreementClauseHtml('Project Investment', terms.projectInvestment)}
      ${agreementClauseHtml('Estimated Energy Production and Savings', terms.energyEstimate)}

      ${agreementTableSectionHtml('Agreed Payment Schedule', `
      <table>
        <thead><tr><th>Payment Stage</th><th>%</th><th>Amount</th><th>Due / Condition</th></tr></thead>
        <tbody>${(agreement.paymentSchedule || []).map((row) => `<tr><td>${escapeHtml(row.stage)}</td><td>${Number(row.percent || 0)}%</td><td>PHP ${formatMoney(row.amount)}</td><td>${escapeHtml(row.condition)}</td></tr>`).join('')}</tbody>
      </table>
      `)}
      ${agreementClauseHtml('Payment Conditions', terms.paymentConditions)}

      ${agreementClauseHtml('Credit Card Payment', terms.creditCardPayment)}
      ${agreementTableSectionHtml('Credit Card Payment Summary', `
      <table>
        <tbody>
          <tr><th>Contract Amount</th><td>PHP ${formatMoney(project.contractPrice)}</td></tr>
          <tr><th>Credit Card Processing Fee (${Number(agreement.creditCardFeeRate || 0)}%)</th><td>PHP ${formatMoney(agreement.creditCardFee)}</td></tr>
          <tr><th>Total Amount if Paid via Credit Card</th><td>PHP ${formatMoney(agreement.creditCardTotal)}</td></tr>
        </tbody>
      </table>
      `)}

      ${agreementClauseHtml('Project Timeline', project.timeline)}

      ${[
        ['Scope of Work', terms.scopeOfWork],
        ['Client Responsibilities', terms.clientResponsibilities],
        ['Structural and Electrical Conditions', terms.structuralElectrical],
        ['Change Orders', terms.changeOrders],
        ['Testing, Commissioning and Turnover', terms.testingTurnover],
        ['Load Management', terms.loadManagement],
        ['System Use and Maintenance', terms.systemUseMaintenance],
        ['Ownership and Risk', terms.ownershipRisk]
      ].map(([title, value]) => agreementClauseHtml(title, value)).join('')}

      ${agreementTableSectionHtml('Warranty Coverage', `
      <table>
        <thead><tr><th>Item</th><th>Warranty Coverage</th></tr></thead>
        <tbody>${(agreement.warranty || []).map((row) => `<tr><td>${escapeHtml(row.item)}</td><td>${escapeHtml(row.coverage)}</td></tr>`).join('')}</tbody>
      </table>
      `)}

      ${[
        ['Cancellation and Suspension', terms.cancellationSuspension],
        ['Performance and Savings Disclaimer', terms.performanceDisclaimer],
        ['Liability', terms.liability],
        ['Dispute Resolution and Governing Law', terms.disputeResolution],
        ['Entire Agreement', terms.entireAgreement],
        ['Important Client Note', terms.importantClientNote]
      ].map(([title, value]) => agreementClauseHtml(title, value)).join('')}

      <div class="print-block">
      <h2>Client Acceptance</h2>
      <div class="signatures">
        <div><span></span><strong>${escapeHtml(agreement.acceptance?.clientName || client.name || '')}</strong><small>Client</small><small>Date: ${escapeHtml(agreement.acceptance?.clientDate || '')}</small></div>
        <div><span></span><strong>${escapeHtml(agreement.acceptance?.contractorRepresentative || 'AUTHORIZED REPRESENTATIVE')}</strong><small>For ${escapeHtml(companyName)}</small><small>Date: ${escapeHtml(agreement.acceptance?.contractorDate || '')}</small></div>
      </div>
      </div>

      ${agreementTableSectionHtml('Turnover Confirmation', `
      <table>
        <tbody>
          <tr><th>System installation completed</th><td>${agreement.turnover?.systemInstallationCompleted ? 'Yes' : 'No'}${agreement.turnover?.withPunchList ? ' - With written punch-list' : ''}</td></tr>
          <tr><th>System operation demonstrated</th><td>${agreement.turnover?.systemOperationDemonstrated ? 'Yes' : 'No'}</td></tr>
          <tr><th>Shutdown and safety procedure explained</th><td>${agreement.turnover?.shutdownSafetyExplained ? 'Yes' : 'No'}</td></tr>
          <tr><th>Monitoring access provided</th><td>${agreement.turnover?.monitoringAccessProvided ? 'Yes' : agreement.turnover?.monitoringNotApplicable ? 'Not applicable' : 'No'}</td></tr>
          <tr><th>Warranty documents received</th><td>${agreement.turnover?.warrantyDocumentsReceived ? 'Yes' : agreement.turnover?.warrantyDocumentsToFollow ? 'To follow' : 'No'}</td></tr>
        </tbody>
      </table>
      `)}

      <div class="clause">
      <h2>Acknowledgment</h2>
      <p>REPUBLIC OF THE PHILIPPINES ) CITY/MUNICIPALITY OF ${escapeHtml(agreement.notary?.cityMunicipality || '')}</p>
      <p>Doc. No. ${escapeHtml(agreement.notary?.docNo || '')}; Page No. ${escapeHtml(agreement.notary?.pageNo || '')}; Book No. ${escapeHtml(agreement.notary?.bookNo || '')}; Series of ${escapeHtml(agreement.notary?.seriesYear || '')}.</p>
      </div>

      <footer>${escapeHtml(companyName)} | ${escapeHtml(companyPhone)} | ${escapeHtml(companyEmail)} | ${escapeHtml(agreement.agreementNumber || '')}</footer>
    </section>
  `;
}

function printAgreement(agreement, company) {
  const popup = window.open('', '_blank');
  if (!popup) return;
  const printTitle = agreementPrintTitle(agreement);
  popup.document.write(`
    <html>
      <head>
        <title>${escapeHtml(printTitle)}</title>
        <style>${AGREEMENT_PRINT_CSS}</style>
      </head>
      <body>${agreementPrintHtml(agreement, company)}</body>
    </html>
  `);
  popup.document.close();
  window.setTimeout(() => popup.print(), 350);
}

function printAgreementPreview(agreement) {
  if (!agreement) return;
  const previousTitle = document.title;
  let restored = false;
  const restoreTitle = () => {
    if (restored) return;
    restored = true;
    document.title = previousTitle;
    window.removeEventListener('afterprint', restoreTitle);
  };

  document.title = agreementPrintTitle(agreement);
  window.addEventListener('afterprint', restoreTitle);
  window.print();
  window.setTimeout(restoreTitle, 5000);
}

function Field({ label, value, onChange, type = 'text', textarea = false }) {
  return (
    <label className="block min-w-0 text-sm">
      <span className="block text-xs font-medium text-gray-500 mb-1">{label}</span>
      {textarea ? (
        <textarea rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full border rounded px-3 py-2" />
      ) : (
        <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full border rounded px-3 py-2" />
      )}
    </label>
  );
}

export default function FormsManager({ authConfig, onUnauthorized, employeeMode = false }) {
  const [formType, setFormType] = useState('agreements');
  const [activeView, setActiveView] = useState('list');
  const [agreements, setAgreements] = useState([]);
  const [commissioningForms, setCommissioningForms] = useState([]);
  const [ocularVisits, setOcularVisits] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [company, setCompany] = useState({});
  const [selectedQuotationId, setSelectedQuotationId] = useState('');
  const [draftQuotationId, setDraftQuotationId] = useState('');
  const [loadingQuotations, setLoadingQuotations] = useState(false);
  const [agreement, setAgreement] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [formKindFilter, setFormKindFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [initialCommissioningId, setInitialCommissioningId] = useState('');
  const [initialOcularId, setInitialOcularId] = useState('');
  const [showAddMenu, setShowAddMenu] = useState(false);

  const selectedQuotation = useMemo(
    () => quotations.find((q) => q._id === selectedQuotationId),
    [quotations, selectedQuotationId]
  );

  const formRecords = useMemo(() => {
    const agreementRecords = agreements.map((row) => ({
      id: row._id,
      source: 'agreement',
      type: 'Solar Agreement',
      number: row.agreementNumber || row._id,
      reference: row.quotationReference || '',
      client: row.client?.name || '-',
      project: row.project?.systemType || row.project?.pvArray || '-',
      date: row.agreementDate,
      status: row.status || 'draft',
      total: Number(row.project?.contractPrice || 0),
      details: row.components || [],
      raw: row
    }));
    const commissioningRecords = commissioningForms.map((row) => ({
      id: row._id,
      source: 'commissioning',
      type: 'Solar Commissioning',
      number: row.checklistNumber || row._id,
      reference: row.checklistNumber || '',
      client: row.clientName || '-',
      project: row.projectName || row.location || '-',
      date: row.commissioningDate,
      status: row.status || 'draft',
      system: `${row.systemSizeKw || 6}kW ${row.phaseType || 'Single phase'}`,
      raw: row
    }));
    const ocularRecords = ocularVisits.map((row) => ({
      id: row._id,
      source: 'ocular',
      type: 'Solar Ocular Visit',
      number: row.ocularNumber || row._id,
      reference: row.ocularNumber || '',
      client: row.clientName || '-',
      project: row.projectName || row.installationAddress || '-',
      date: row.visitDate,
      status: row.status || 'draft',
      system: row.siteInspector || '-',
      raw: row
    }));
    return [...agreementRecords, ...commissioningRecords, ...ocularRecords];
  }, [agreements, commissioningForms, ocularVisits]);

  const filteredFormRecords = useMemo(() => {
    const term = search.trim().toLowerCase();
    return formRecords.filter((record) => {
      if (formKindFilter !== 'all' && record.source !== formKindFilter) return false;
      if (statusFilter !== 'all' && record.status !== statusFilter) return false;
      if (dateFrom && dateInputValue(record.date) < dateFrom) return false;
      if (dateTo && dateInputValue(record.date) > dateTo) return false;
      return recordIncludes(record, term);
    });
  }, [dateFrom, dateTo, formKindFilter, formRecords, search, statusFilter]);

  const loadAgreements = useCallback(async () => {
    setLoading(true);
    try {
      const params = search.trim() ? { reference: search.trim() } : {};
      const [agreementList, commissioningList, ocularList] = await Promise.all([
        fetchSolarAgreements(params, authConfig),
        fetchSolarCommissioningForms(params, authConfig),
        fetchSolarOcularVisits(params, authConfig)
      ]);
      setAgreements(agreementList);
      setCommissioningForms(commissioningList);
      setOcularVisits(ocularList);
      setError('');
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to load forms.');
    } finally {
      setLoading(false);
    }
  }, [authConfig, onUnauthorized, search]);

  const loadQuotations = useCallback(async () => {
    setLoadingQuotations(true);
    try {
      const list = employeeMode
        ? await fetchEmployeeFormQuotations({}, authConfig)
        : await fetchDocuments({ type: 'proforma' }, authConfig);
      setQuotations(list);
      setSelectedQuotationId((prev) => list.some((quotation) => quotation._id === prev) ? prev : list[0]?._id || '');
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to load quotations.');
    } finally {
      setLoadingQuotations(false);
    }
  }, [authConfig, onUnauthorized, employeeMode]);

  useEffect(() => {
    loadAgreements();
    const fetchCompany = employeeMode ? getEmployeeFormsCompany : getCompany;
    fetchCompany(authConfig).then((data) => setCompany(data || {})).catch(() => setCompany({}));
  }, [loadAgreements, authConfig, employeeMode]);

  useEffect(() => {
    setSelectedForm((current) => {
      if (current && filteredFormRecords.some((record) => record.id === current.id && record.source === current.source)) {
        return current;
      }
      return filteredFormRecords[0] || null;
    });
  }, [filteredFormRecords]);

  useEffect(() => {
    if (activeView === 'create') loadQuotations();
  }, [activeView, loadQuotations]);

  const openAgreement = async (id) => {
    setLoading(true);
    try {
      const data = await fetchSolarAgreementById(id, authConfig);
      setAgreement(normalizeAgreement(data));
      setDraftQuotationId('');
      setActiveView(employeeMode ? 'preview' : 'edit');
      setError('');
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to open solar agreement.');
    } finally {
      setLoading(false);
    }
  };

  const openFormRecord = async (record = selectedForm) => {
    if (!record) return;
    if (record.source === 'agreement') {
      await openAgreement(record.id);
      return;
    }
    if (record.source === 'commissioning') {
      setInitialCommissioningId(record.id);
      setFormType('commissioning');
      setActiveView('list');
      return;
    }
    if (record.source === 'ocular') {
      setInitialOcularId(record.id);
      setFormType('ocular');
      setActiveView('list');
    }
  };

  const previewFormRecord = async () => {
    if (!selectedForm) return;
    if (selectedForm.source !== 'agreement') {
      await openFormRecord(selectedForm);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchSolarAgreementById(selectedForm.id, authConfig);
      setAgreement(normalizeAgreement(data));
      setActiveView('preview');
      setError('');
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to preview solar agreement.');
    } finally {
      setLoading(false);
    }
  };

  const printFormRecord = async () => {
    if (!selectedForm) return;
    if (selectedForm.source !== 'agreement') {
      await openFormRecord(selectedForm);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchSolarAgreementById(selectedForm.id, authConfig);
      printAgreement(normalizeAgreement(data), company);
      setError('');
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to print solar agreement.');
    } finally {
      setLoading(false);
    }
  };

  const removeFormRecord = async () => {
    if (employeeMode || !selectedForm || !window.confirm(`Delete ${selectedForm.number || 'this form'}?`)) return;
    setSaving(true);
    try {
      if (selectedForm.source === 'agreement') await deleteSolarAgreement(selectedForm.id, authConfig);
      if (selectedForm.source === 'commissioning') await deleteSolarCommissioning(selectedForm.id, authConfig);
      if (selectedForm.source === 'ocular') await deleteSolarOcularVisit(selectedForm.id, authConfig);
      setSelectedForm(null);
      await loadAgreements();
      setError('');
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to delete form.');
    } finally {
      setSaving(false);
    }
  };

  const startAddForm = () => {
    setShowAddMenu(true);
  };

  const closeAddMenu = () => {
    setShowAddMenu(false);
  };

  const chooseAddForm = (type) => {
    setShowAddMenu(false);
    if (type === 'agreement') {
      setFormType('agreements');
      setActiveView('list');
      setAgreement(null);
      setFormKindFilter('agreement');
      loadAgreements();
      return;
    }
    if (type === 'agreementFromQuotation') {
      setFormType('agreements');
      setAgreement(null);
      setDraftQuotationId('');
      setActiveView('create');
      return;
    }
    if (type === 'commissioning') {
      setInitialCommissioningId('');
      setFormType('commissioning');
      return;
    }
    if (type === 'ocular') {
      setInitialOcularId('');
      setFormType('ocular');
    }
  };

  const createFromQuotation = async () => {
    if (saving) return;
    if (!selectedQuotationId) {
      setError('Select a quotation first.');
      return;
    }
    setSaving(true);
    try {
      const data = employeeMode
        ? await previewSolarAgreementFromQuotation(selectedQuotationId, authConfig)
        : await createSolarAgreementFromQuotation(selectedQuotationId, authConfig);
      setAgreement(normalizeAgreement(data));
      setDraftQuotationId(employeeMode ? selectedQuotationId : '');
      setActiveView('edit');
      if (!employeeMode) await loadAgreements();
      setError('');
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to create agreement from quotation.');
    } finally {
      setSaving(false);
    }
  };

  const patchAgreement = (path, value) => {
    if (employeeMode && agreement?._id) return;
    setAgreement((current) => {
      const next = { ...current };
      const keys = path.split('.');
      let target = next;
      for (let i = 0; i < keys.length - 1; i += 1) {
        target[keys[i]] = { ...(target[keys[i]] || {}) };
        target = target[keys[i]];
      }
      target[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const updateArrayRow = (key, index, field, value) => {
    if (employeeMode && agreement?._id) return;
    setAgreement((current) => ({
      ...current,
      [key]: (current[key] || []).map((row, rowIndex) => (
        rowIndex === index ? { ...row, [field]: value } : row
      ))
    }));
  };

  const addArrayRow = (key, row) => {
    if (employeeMode && agreement?._id) return;
    setAgreement((current) => ({
      ...current,
      [key]: [...(current[key] || []), row]
    }));
  };

  const removeArrayRow = (key, index) => {
    if (employeeMode && agreement?._id) return;
    setAgreement((current) => ({
      ...current,
      [key]: (current[key] || []).filter((_, rowIndex) => rowIndex !== index)
    }));
  };

  const saveAgreement = async () => {
    if (saving || !agreement) return;
    if (employeeMode ? (agreement._id || !draftQuotationId) : !agreement._id) return;
    setSaving(true);
    try {
      const saved = employeeMode
        ? await createSolarAgreementFromQuotation(draftQuotationId, authConfig, agreement)
        : await updateSolarAgreement(agreement._id, agreement, authConfig);
      setAgreement(normalizeAgreement(saved));
      if (employeeMode) {
        setDraftQuotationId('');
        setActiveView('preview');
      }
      await loadAgreements();
      setError('');
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to save solar agreement.');
    } finally {
      setSaving(false);
    }
  };

  const removeAgreement = async () => {
    if (employeeMode || !agreement?._id || !window.confirm('Delete this solar agreement draft?')) return;
    setSaving(true);
    try {
      await deleteSolarAgreement(agreement._id, authConfig);
      setAgreement(null);
      setActiveView('list');
      await loadAgreements();
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to delete solar agreement.');
    } finally {
      setSaving(false);
    }
  };

  const previewAgreement = () => {
    if (!agreement) return null;
    return (
      <div className="bg-gray-100 rounded-lg overflow-hidden flex flex-col h-[calc(100vh-9rem)] print:bg-white print:h-auto">
        <div className="flex flex-col gap-2 border-b bg-white px-3 py-2 print:hidden sm:flex-row sm:items-center sm:justify-between">
          <button type="button" onClick={() => setActiveView(employeeMode && agreement._id ? 'list' : 'edit')} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100">{employeeMode ? (agreement._id ? 'Back to forms' : 'Back to form') : 'Back to edit'}</button>
          <div className="grid grid-cols-2 gap-2 sm:flex">
            <button type="button" onClick={() => printAgreementPreview(agreement)} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100">Print</button>
            <button type="button" onClick={() => printAgreementPreview(agreement)} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100">Save PDF</button>
          </div>
        </div>
        <style>{`
          @page { size: A4 portrait; margin: 12mm 14mm; }
          @media print {
            body * { visibility: hidden; }
            #solar-agreement-print, #solar-agreement-print * { visibility: visible; }
            #solar-agreement-print { position: absolute; left: 0; top: 0; width: auto !important; min-height: auto !important; margin: 0 !important; padding: 0 !important; box-shadow: none !important; }
            #solar-agreement-print .sheet { width: auto !important; min-height: auto !important; margin: 0 !important; padding: 0 !important; box-shadow: none !important; }
          }
          #solar-agreement-print .sheet { width: 210mm; min-height: 297mm; margin: 0 auto; padding: 12mm 14mm; background: #fff; }
          #solar-agreement-print table { width: 100%; border-collapse: collapse; margin: 4px 0 8px; break-inside: auto; page-break-inside: auto; }
          #solar-agreement-print thead { display: table-header-group; }
          #solar-agreement-print tfoot { display: table-footer-group; }
          #solar-agreement-print tr { break-inside: avoid; page-break-inside: avoid; }
          #solar-agreement-print th, #solar-agreement-print td { border: 1px solid #d1d5db; padding: 3.5px 5px; vertical-align: top; text-align: left; }
          #solar-agreement-print th { background: #f3f4f6; font-weight: 700; }
          #solar-agreement-print h1 { font-size: 14pt; margin: 0 0 4px; text-transform: uppercase; line-height: 1.18; }
          #solar-agreement-print h2 { font-size: 10pt; margin: 10px 0 4px; text-transform: uppercase; line-height: 1.2; }
          #solar-agreement-print p { margin: 0 0 5px; text-align: justify; orphans: 3; widows: 3; }
          #solar-agreement-print .header { display: grid; grid-template-columns: minmax(0, 1fr) 47mm; align-items: start; gap: 10mm; border-bottom: 1px solid #9ca3af; padding-bottom: 7px; margin-bottom: 10px; break-inside: avoid; page-break-inside: avoid; }
          #solar-agreement-print .agreement-logo { width: 47mm; height: 34mm; overflow: hidden; justify-self: end; background: #fff; }
          #solar-agreement-print .agreement-logo img { width: 50mm; height: 34mm; object-fit: fill; transform: translateX(-3mm); display: block; }
          #solar-agreement-print .agreement-meta { display: grid; grid-template-columns: max-content minmax(0, 1fr); gap: 2px 8px; margin-top: 4px; font-size: 8.4pt; line-height: 1.25; }
          #solar-agreement-print .agreement-meta strong { white-space: nowrap; }
          #solar-agreement-print .agreement-meta span { overflow-wrap: anywhere; }
          #solar-agreement-print .muted { color: #4b5563; }
          #solar-agreement-print .summary th { width: 34%; }
          #solar-agreement-print .print-block, #solar-agreement-print .clause, #solar-agreement-print .signatures, #solar-agreement-print footer { break-inside: avoid; page-break-inside: avoid; }
          #solar-agreement-print .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin: 18px 0 10px; }
          #solar-agreement-print .signatures span { display: block; border-top: 1px solid #111827; height: 1px; margin: 30px 0 6px; }
          #solar-agreement-print .signatures small { display: block; color: #374151; }
          #solar-agreement-print footer { margin-top: 14px; padding-top: 6px; border-top: 1px solid #d1d5db; font-size: 8pt; color: #4b5563; }
        `}</style>
        <div className="flex flex-1 justify-start overflow-auto p-3 print:p-0 sm:justify-center sm:p-6">
          <div
            id="solar-agreement-print"
            className="shrink-0 text-[9.5pt] leading-snug print:shadow-none"
            style={{ width: '210mm', minHeight: '297mm' }}
            dangerouslySetInnerHTML={{ __html: agreementPrintHtml(agreement, company) }}
          />
        </div>
      </div>
    );
  };

  const renderArrayEditor = (key, columns, blankRow) => (
    <div className="border rounded overflow-auto">
      <table className="w-full min-w-[680px] text-sm">
        <thead>
          <tr className="bg-gray-100">
            {columns.map((col) => <th key={col.field} className="text-left p-2">{col.label}</th>)}
            <th className="w-16 p-2" />
          </tr>
        </thead>
        <tbody>
          {(agreement[key] || []).map((row, index) => (
            <tr key={index} className="border-t">
              {columns.map((col) => (
                <td key={col.field} className="p-2">
                  <input
                    type={col.type || 'text'}
                    value={row[col.field] || ''}
                    onChange={(e) => updateArrayRow(key, index, col.field, col.type === 'number' ? Number(e.target.value) : e.target.value)}
                    className="w-full rounded border px-3 py-2"
                  />
                </td>
              ))}
              <td className="p-2">
                <button type="button" onClick={() => removeArrayRow(key, index)} className="text-xs text-red-600 hover:underline">Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" onClick={() => addArrayRow(key, blankRow)} className="m-2 min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100">Add row</button>
    </div>
  );

  if (activeView === 'preview') return previewAgreement();

  if (formType === 'commissioning') {
    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-800">{employeeMode ? 'Employee Portal' : 'Management'} &gt; Forms</h2>
            <p className="text-sm text-gray-500">Solar Agreements, Solar Commissioning checklists, and Ocular Visit checklists.</p>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <button
              type="button"
              onClick={() => {
                setFormType('agreements');
                setActiveView('list');
                setAgreement(null);
                loadAgreements();
              }}
              className="min-h-10 px-3 py-2 rounded text-sm bg-gray-100 hover:bg-gray-200"
            >
              Solar Agreements
            </button>
            <button type="button" className="min-h-10 px-3 py-2 rounded text-sm bg-indigo-600 text-white">Solar Commissioning</button>
            <button type="button" onClick={() => setFormType('ocular')} className="min-h-10 px-3 py-2 rounded text-sm bg-gray-100 hover:bg-gray-200">Solar Ocular Visit</button>
          </div>
        </div>
        <SolarCommissioningManager authConfig={authConfig} onUnauthorized={onUnauthorized} company={company} initialOpenId={initialCommissioningId} employeeMode={employeeMode} />
      </div>
    );
  }

  if (formType === 'ocular') {
    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-800">{employeeMode ? 'Employee Portal' : 'Management'} &gt; Forms</h2>
            <p className="text-sm text-gray-500">Solar Agreements, Solar Commissioning checklists, and Ocular Visit checklists.</p>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <button
              type="button"
              onClick={() => {
                setFormType('agreements');
                setActiveView('list');
                setAgreement(null);
                loadAgreements();
              }}
              className="min-h-10 px-3 py-2 rounded text-sm bg-gray-100 hover:bg-gray-200"
            >
              Solar Agreements
            </button>
            <button type="button" onClick={() => setFormType('commissioning')} className="min-h-10 px-3 py-2 rounded text-sm bg-gray-100 hover:bg-gray-200">Solar Commissioning</button>
            <button type="button" className="min-h-10 px-3 py-2 rounded text-sm bg-indigo-600 text-white">Solar Ocular Visit</button>
          </div>
        </div>
        <SolarOcularVisitManager authConfig={authConfig} onUnauthorized={onUnauthorized} company={company} initialOpenId={initialOcularId} employeeMode={employeeMode} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-800">{employeeMode ? 'Employee Portal' : 'Management'} &gt; Forms</h2>
          <p className="text-sm text-gray-500">Solar Agreements, Solar Commissioning checklists, and Ocular Visit checklists.</p>
          {employeeMode && <p className="text-sm text-gray-600">Create new forms and view your saved forms. Contact management for edits or deletions.</p>}
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {showAddMenu && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-3xl rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Add form</h3>
                <p className="text-sm text-gray-500">{employeeMode ? 'Choose a form to create.' : 'Choose what form you want to create or open.'}</p>
              </div>
              <button type="button" onClick={closeAddMenu} className="rounded px-3 py-1 text-xl leading-none text-gray-500 hover:bg-gray-100" aria-label="Close add form menu">x</button>
            </div>
            <div className="grid gap-3 p-5 sm:grid-cols-2">
              {!employeeMode && <button
                type="button"
                onClick={() => chooseAddForm('agreement')}
                className="min-h-28 rounded-lg border p-4 text-left hover:border-indigo-300 hover:bg-indigo-50"
              >
                <span className="block font-semibold text-gray-900">Solar Agreements</span>
                <span className="mt-2 block text-sm text-gray-500">View and manage saved solar agreements.</span>
              </button>}
              <button
                type="button"
                onClick={() => chooseAddForm('agreementFromQuotation')}
                className="min-h-28 rounded-lg border p-4 text-left hover:border-indigo-300 hover:bg-indigo-50"
              >
                <span className="block font-semibold text-gray-900">Create SA from quotation</span>
                <span className="mt-2 block text-sm text-gray-500">Generate a solar agreement using a saved quotation.</span>
              </button>
              <button
                type="button"
                onClick={() => chooseAddForm('commissioning')}
                className="min-h-28 rounded-lg border p-4 text-left hover:border-indigo-300 hover:bg-indigo-50"
              >
                <span className="block font-semibold text-gray-900">Solar Commissioning</span>
                <span className="mt-2 block text-sm text-gray-500">Open or create a commissioning checklist.</span>
              </button>
              <button
                type="button"
                onClick={() => chooseAddForm('ocular')}
                className="min-h-28 rounded-lg border p-4 text-left hover:border-indigo-300 hover:bg-indigo-50"
              >
                <span className="block font-semibold text-gray-900">Solar Ocular Visit</span>
                <span className="mt-2 block text-sm text-gray-500">Open or create an ocular visit checklist.</span>
              </button>
            </div>
            <div className="flex justify-end border-t px-5 py-4">
              <button type="button" onClick={closeAddMenu} className="min-h-10 rounded border px-4 py-2 text-sm hover:bg-gray-100">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {activeView === 'list' && (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 rounded border bg-white p-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={startAddForm} className="min-h-10 rounded bg-indigo-600 px-4 py-2 text-sm text-white">+ Add</button>
              <button type="button" onClick={printFormRecord} disabled={!selectedForm || loading} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50">Print</button>
              <button type="button" onClick={previewFormRecord} disabled={!selectedForm || loading} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50">Print preview</button>
              <button type="button" onClick={printFormRecord} disabled={!selectedForm || loading} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50">Save as PDF</button>
              {!employeeMode && <button type="button" onClick={() => openFormRecord()} disabled={!selectedForm || loading} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50">Open / Edit</button>}
              {!employeeMode && <button type="button" onClick={removeFormRecord} disabled={!selectedForm || saving} className="min-h-10 rounded border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50">Delete</button>}
            </div>
            <button type="button" onClick={loadAgreements} disabled={loading} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50">
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-gray-500">Find:</span>
            {[
              ['all', 'All forms'],
              ['agreement', 'Solar Agreements'],
              ['commissioning', 'Solar Commissioning'],
              ['ocular', 'Solar Ocular Visit']
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setFormKindFilter(value)}
                className={`min-h-9 rounded border px-3 py-1.5 ${formKindFilter === value ? 'border-indigo-600 bg-indigo-600 text-white' : 'bg-white hover:bg-gray-100'}`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-3 rounded border bg-gray-50 p-3 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1.4fr_auto_auto] xl:items-end">
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-gray-500">Form type</span>
              <select value={formKindFilter} onChange={(e) => setFormKindFilter(e.target.value)} className="min-h-10 w-full rounded border px-3 py-2">
                <option value="all">All form types</option>
                <option value="agreement">Solar Agreements</option>
                <option value="commissioning">Solar Commissioning</option>
                <option value="ocular">Solar Ocular Visit</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-gray-500">Status</span>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="min-h-10 w-full rounded border px-3 py-2">
                <option value="all">All statuses</option>
                <option value="draft">Draft</option>
                <option value="completed">Completed</option>
                <option value="final">Final</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </label>
            <Field label="Search by number, reference, client, project" value={search} onChange={setSearch} />
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-gray-500">From</span>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="min-h-10 w-full rounded border px-3 py-2" />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-gray-500">To</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="min-h-10 w-full rounded border px-3 py-2" />
            </label>
            <div className="flex gap-2 md:col-span-2 xl:col-span-5">
              <button type="button" onClick={loadAgreements} disabled={loading} className="min-h-10 rounded bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-50">Search</button>
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setStatusFilter('all');
                  setDateFrom('');
                  setDateTo('');
                  setFormKindFilter('all');
                }}
                className="min-h-10 rounded border bg-white px-4 py-2 text-sm hover:bg-gray-100"
              >
                Clear all filters
              </button>
            </div>
          </div>

          <div className="rounded border bg-white">
            <div className="border-b bg-gray-100 px-3 py-2 font-semibold">Forms ({filteredFormRecords.length})</div>
            <div className="overflow-auto">
              <table className="w-full min-w-[980px] text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-2 text-left">ID</th>
                  <th className="p-2 text-left">Number</th>
                  <th className="p-2 text-left">Form type</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Client / Project</th>
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-left">Reference</th>
                  <th className="p-2 text-right">System / Total</th>
                </tr>
              </thead>
              <tbody>
                {filteredFormRecords.map((record) => {
                  const selected = selectedForm?.id === record.id && selectedForm?.source === record.source;
                  return (
                    <tr
                      key={`${record.source}-${record.id}`}
                      onClick={() => setSelectedForm(record)}
                      onDoubleClick={() => openFormRecord(record)}
                      className={`cursor-pointer border-t ${selected ? 'bg-indigo-100' : 'hover:bg-gray-50'}`}
                    >
                      <td className="p-2 text-xs text-gray-500">{String(record.id || '').slice(-6)}</td>
                      <td className="p-2 font-medium">{record.number || '-'}</td>
                      <td className="p-2">{record.type}</td>
                      <td className="p-2 capitalize">{record.status || '-'}</td>
                      <td className="p-2">
                        <div className="font-medium">{record.client || '-'}</div>
                        <div className="truncate text-xs text-gray-500">{record.project || '-'}</div>
                      </td>
                      <td className="p-2">{formatDate(record.date)}</td>
                      <td className="p-2">{record.reference || '-'}</td>
                      <td className="p-2 text-right">{record.source === 'agreement' ? `P${formatMoney(record.total)}` : (record.system || '-')}</td>
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>
            {!loading && filteredFormRecords.length === 0 && <p className="p-4 text-center text-sm text-gray-500">No forms found.</p>}
            {loading && <p className="p-4 text-center text-sm text-gray-500">Loading forms...</p>}
          </div>

          <div className="rounded border bg-white">
            <div className="border-b bg-gray-100 px-3 py-2 font-semibold">
              Form details {selectedForm ? `- ${selectedForm.number || selectedForm.type}` : ''}
            </div>
            {!selectedForm ? (
              <p className="p-4 text-sm text-gray-500">Select a form to view its details.</p>
            ) : selectedForm.source === 'agreement' ? (
              <div className="overflow-auto">
                <table className="w-full min-w-[720px] text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="p-2 text-left">#</th>
                      <th className="p-2 text-left">Item / Scope</th>
                      <th className="p-2 text-left">Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedForm.details || []).map((item, index) => (
                      <tr key={`${selectedForm.id}-component-${index}`} className="border-t">
                        <td className="p-2">{index + 1}</td>
                        <td className="p-2">{item.item || '-'}</td>
                        <td className="p-2">{item.quantity || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(selectedForm.details || []).length === 0 && <p className="p-4 text-sm text-gray-500">No component details saved.</p>}
              </div>
            ) : (
              <div className="grid gap-3 p-4 text-sm md:grid-cols-2 lg:grid-cols-3">
                <p><span className="block text-xs font-medium uppercase text-gray-500">Form type</span>{selectedForm.type}</p>
                <p><span className="block text-xs font-medium uppercase text-gray-500">Status</span><span className="capitalize">{selectedForm.status || '-'}</span></p>
                <p><span className="block text-xs font-medium uppercase text-gray-500">Date</span>{formatDate(selectedForm.date)}</p>
                <p><span className="block text-xs font-medium uppercase text-gray-500">Client</span>{selectedForm.client || '-'}</p>
                <p><span className="block text-xs font-medium uppercase text-gray-500">Project / Site</span>{selectedForm.project || '-'}</p>
                <p><span className="block text-xs font-medium uppercase text-gray-500">System / Inspector</span>{selectedForm.system || '-'}</p>
                <p><span className="block text-xs font-medium uppercase text-gray-500">Reference</span>{selectedForm.reference || '-'}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeView === 'create' && (
        <div className="border rounded p-4 bg-gray-50 space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="font-semibold">Create Solar Agreement from Proforma / Quotation</h3>
            <button
              type="button"
              onClick={() => {
                setActiveView('list');
                setAgreement(null);
              }}
              className="min-h-10 rounded border bg-white px-3 py-2 text-sm hover:bg-gray-100"
            >
              Back to forms
            </button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-end">
            <label className="block min-w-0 text-sm">
              <span className="block text-xs font-medium text-gray-500 mb-1">Select quotation</span>
              <select value={selectedQuotationId} onChange={(e) => setSelectedQuotationId(e.target.value)} className="w-full border rounded px-3 py-2">
                <option value="">Select a quotation</option>
                {quotations.map((q) => (
                  <option key={q._id} value={q._id}>
                    {(q.reference || q._id)} - {q.customerName || 'Walk-in'} - P{formatMoney(q.totalAmount || (q.items || []).reduce((s, item) => s + (Number(item.quantity) || 0) * (Number(item.unitCost) || 0), 0))}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" onClick={createFromQuotation} disabled={saving || loadingQuotations || !selectedQuotationId} className="min-h-10 px-4 py-2 rounded bg-indigo-600 text-white disabled:opacity-60">
              {employeeMode ? (saving ? 'Preparing...' : 'Prepare agreement') : (saving ? 'Creating...' : 'Create agreement')}
            </button>
          </div>
          {loadingQuotations && <p className="text-sm text-gray-500">Loading quotations...</p>}
          {employeeMode && !loadingQuotations && quotations.length === 0 && (
            <p className="text-sm text-gray-600">No quotations are available for your assigned store. Ask management to add a quotation or check your store assignment.</p>
          )}
          {selectedQuotation && (
            <div className="border rounded bg-white p-3 text-sm">
              <p><strong>Customer:</strong> {selectedQuotation.customerName || 'Walk-in'}</p>
              {!employeeMode && <p><strong>Address:</strong> {selectedQuotation.customerAddress || '-'}</p>}
              {!employeeMode && <p><strong>Items:</strong> {(selectedQuotation.items || []).length}</p>}
              <p><strong>Total:</strong> P{formatMoney(selectedQuotation.totalAmount || (selectedQuotation.items || []).reduce((s, item) => s + (Number(item.quantity) || 0) * (Number(item.unitCost) || 0), 0))}</p>
            </div>
          )}
        </div>
      )}

      {activeView === 'edit' && agreement && (!employeeMode || !agreement._id) && (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 border rounded p-3 bg-gray-50 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <h3 className="font-semibold">Solar Agreement {agreement.agreementNumber}</h3>
              <p className="text-sm text-gray-500">Source quotation: {agreement.quotationReference || '-'}</p>
              {employeeMode && <p className="text-sm text-gray-600">Complete this form before saving. Saved forms can be viewed and printed; only management can edit or delete them.</p>}
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5 lg:flex lg:flex-wrap">
              <button type="button" onClick={() => setActiveView('preview')} className="min-h-10 px-3 py-2 rounded border hover:bg-gray-100">Preview</button>
              <button type="button" onClick={() => printAgreement(agreement, company)} className="min-h-10 px-3 py-2 rounded border hover:bg-gray-100">Print / PDF</button>
              <button type="button" onClick={saveAgreement} disabled={saving} className="min-h-10 px-3 py-2 rounded bg-indigo-600 text-white disabled:opacity-60">{saving ? 'Saving...' : (employeeMode ? 'Create form' : 'Save')}</button>
              {!employeeMode && <button type="button" onClick={removeAgreement} disabled={saving || agreement.status === 'final'} className="min-h-10 px-3 py-2 rounded border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50">Delete</button>}
              <button type="button" onClick={() => setActiveView('list')} className="min-h-10 px-3 py-2 rounded border hover:bg-gray-100">Close</button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <section className="border rounded p-4 bg-white space-y-3">
              <h4 className="font-semibold">Agreement details</h4>
              <Field label="Status" value={agreement.status} onChange={(value) => patchAgreement('status', value)} />
              <Field label="Agreement date" type="date" value={agreement.agreementDate} onChange={(value) => patchAgreement('agreementDate', value)} />
              <Field label="Quotation reference" value={agreement.quotationReference} onChange={(value) => patchAgreement('quotationReference', value)} />
            </section>
            <section className="border rounded p-4 bg-white space-y-3 lg:col-span-2">
              <h4 className="font-semibold">Client details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Client name" value={agreement.client.name} onChange={(value) => patchAgreement('client.name', value)} />
                <Field label="Phone" value={agreement.client.phone} onChange={(value) => patchAgreement('client.phone', value)} />
                <Field label="Email" value={agreement.client.email} onChange={(value) => patchAgreement('client.email', value)} />
                <Field label="Project address" value={agreement.client.address} onChange={(value) => patchAgreement('client.address', value)} />
              </div>
            </section>
          </div>

          <section className="border rounded p-4 bg-white space-y-3">
            <h4 className="font-semibold">Project summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <Field label="System type" value={agreement.project.systemType} onChange={(value) => patchAgreement('project.systemType', value)} />
              <Field label="PV array" value={agreement.project.pvArray} onChange={(value) => patchAgreement('project.pvArray', value)} />
              <Field label="Battery storage" value={agreement.project.batteryStorage} onChange={(value) => patchAgreement('project.batteryStorage', value)} />
              <Field label="Inverter" value={agreement.project.inverter} onChange={(value) => patchAgreement('project.inverter', value)} />
              <Field label="Contract price" type="number" value={agreement.project.contractPrice} onChange={(value) => patchAgreement('project.contractPrice', Number(value))} />
              <Field label="Invoice/payment status" value={agreement.project.invoiceStatus} onChange={(value) => patchAgreement('project.invoiceStatus', value)} />
            </div>
            <Field label="Project timeline" value={agreement.project.timeline} onChange={(value) => patchAgreement('project.timeline', value)} textarea />
          </section>

          <section className="border rounded p-4 bg-white space-y-3">
            <h4 className="font-semibold">System components and scope</h4>
            {renderArrayEditor('components', [
              { field: 'item', label: 'Item / Scope' },
              { field: 'quantity', label: 'Quantity' }
            ], { item: '', quantity: '' })}
          </section>

          <section className="border rounded p-4 bg-white space-y-3">
            <h4 className="font-semibold">System capacity</h4>
            {renderArrayEditor('capacity', [
              { field: 'label', label: 'Label' },
              { field: 'value', label: 'Value' }
            ], { label: '', value: '' })}
          </section>

          <section className="border rounded p-4 bg-white space-y-3">
            <h4 className="font-semibold">Payment schedule</h4>
            {renderArrayEditor('paymentSchedule', [
              { field: 'stage', label: 'Stage' },
              { field: 'percent', label: '%', type: 'number' },
              { field: 'amount', label: 'Amount', type: 'number' },
              { field: 'condition', label: 'Due / Condition' }
            ], { stage: '', percent: 0, amount: 0, condition: '' })}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Credit card fee rate %" type="number" value={agreement.creditCardFeeRate} onChange={(value) => patchAgreement('creditCardFeeRate', Number(value))} />
              <Field label="Credit card fee" type="number" value={agreement.creditCardFee} onChange={(value) => patchAgreement('creditCardFee', Number(value))} />
              <Field label="Credit card total" type="number" value={agreement.creditCardTotal} onChange={(value) => patchAgreement('creditCardTotal', Number(value))} />
            </div>
          </section>

          <section className="border rounded p-4 bg-white space-y-3">
            <h4 className="font-semibold">Warranty coverage</h4>
            {renderArrayEditor('warranty', [
              { field: 'item', label: 'Item' },
              { field: 'coverage', label: 'Coverage' }
            ], { item: '', coverage: '' })}
          </section>

          <section className="border rounded p-4 bg-white space-y-3">
            <h4 className="font-semibold">Terms and conditions</h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {TERM_FIELDS.map(([key, label]) => (
                <Field key={key} label={label} value={agreement.terms[key]} onChange={(value) => patchAgreement(`terms.${key}`, value)} textarea />
              ))}
            </div>
          </section>

          <section className="border rounded p-4 bg-white space-y-3">
            <h4 className="font-semibold">Acceptance, turnover, and notary</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <Field label="Client acceptance name" value={agreement.acceptance.clientName} onChange={(value) => patchAgreement('acceptance.clientName', value)} />
              <Field label="Contractor representative" value={agreement.acceptance.contractorRepresentative} onChange={(value) => patchAgreement('acceptance.contractorRepresentative', value)} />
              <Field label="Client acceptance date" value={agreement.acceptance.clientDate} onChange={(value) => patchAgreement('acceptance.clientDate', value)} />
              <Field label="Contractor acceptance date" value={agreement.acceptance.contractorDate} onChange={(value) => patchAgreement('acceptance.contractorDate', value)} />
              <Field label="Witness 1" value={agreement.acceptance.witnessOne} onChange={(value) => patchAgreement('acceptance.witnessOne', value)} />
              <Field label="Witness 2" value={agreement.acceptance.witnessTwo} onChange={(value) => patchAgreement('acceptance.witnessTwo', value)} />
              <Field label="Notary city / municipality" value={agreement.notary.cityMunicipality} onChange={(value) => patchAgreement('notary.cityMunicipality', value)} />
              <Field label="Doc no." value={agreement.notary.docNo} onChange={(value) => patchAgreement('notary.docNo', value)} />
              <Field label="Page no." value={agreement.notary.pageNo} onChange={(value) => patchAgreement('notary.pageNo', value)} />
              <Field label="Book no." value={agreement.notary.bookNo} onChange={(value) => patchAgreement('notary.bookNo', value)} />
              <Field label="Series year" value={agreement.notary.seriesYear} onChange={(value) => patchAgreement('notary.seriesYear', value)} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
              {[
                ['turnover.systemInstallationCompleted', 'System installation completed'],
                ['turnover.withPunchList', 'With written punch-list'],
                ['turnover.systemOperationDemonstrated', 'System operation demonstrated'],
                ['turnover.shutdownSafetyExplained', 'Shutdown and safety explained'],
                ['turnover.monitoringAccessProvided', 'Monitoring access provided'],
                ['turnover.monitoringNotApplicable', 'Monitoring not applicable'],
                ['turnover.warrantyDocumentsReceived', 'Warranty documents received'],
                ['turnover.warrantyDocumentsToFollow', 'Warranty documents to follow']
              ].map(([path, label]) => {
                const keys = path.split('.');
                const value = keys.reduce((obj, key) => obj?.[key], agreement);
                return (
                  <label key={path} className="flex items-center gap-2">
                    <input type="checkbox" checked={Boolean(value)} onChange={(e) => patchAgreement(path, e.target.checked)} />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
